import { ErrorMessage, Field } from "formik";
import { IMAGE_URL } from "../../config";
import { useState, useRef, useEffect } from "react";
import get from "lodash/get";
import set from "lodash/set";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormVideoField = ({
  label = "Upload Video",
  name = "videoField",
  className = "",
  accept = "video/*",
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const uploadFile = (file, form, name) => {
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);

      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        const filePath = response?.data?.filePath;

        const fullUrl = filePath.startsWith("http")
          ? filePath
          : `https://api.requal.in${filePath}`;

        const updatedValues = { ...form.values };
        set(updatedValues, name, {
          url: fullUrl,
          name: file.name,
        });
        form.setValues(updatedValues);
      } else {
        alert("Video upload failed!");
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      alert("An error occurred during upload!");
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files[0];
    uploadFile(file, form, name);
  };

  const handleRemoveVideo = (form, name) => {
    const updatedValues = { ...form.values };
    set(updatedValues, name, null);
    form.setValues(updatedValues);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const fileData = get(form.values, name);
        const error = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-2 w-full ${className}`}>
            <label className="font-medium text-gray-800">
              {formatUnderscoreToSpace(label)}
            </label>

            {!fileData ? (
              <div
                className={`border-2 rounded-lg p-4 transition-all w-[260px] h-[180px] relative overflow-hidden 
                ${
                  isDragActive
                    ? "border-primary bg-purple-50"
                    : "border-gray-300 bg-gray-50 hover:border-primary"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
              >
                {previewUrl && (
                  <video
                    src={previewUrl}
                    className="absolute inset-0 w-full h-full object-cover"
                    muted
                    playsInline
                  />
                )}

                {isUploading && (
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white z-10">
                    <p className="text-sm mb-2 font-medium">Uploading...</p>

                    <div className="w-full max-w-[180px] bg-white/40 rounded-full h-[6px] overflow-hidden">
                      <div
                        className="h-full rounded-full bg-green-500 transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>

                    <p className="text-xs mt-2">{uploadProgress}%</p>
                  </div>
                )}

                {!previewUrl && !isUploading && (
                  <div className="flex flex-col justify-center items-center h-full z-10 relative">
                    <p className="text-gray-600 text-sm text-center">
                      Drag & drop video or{" "}
                      <span
                        onClick={() => inputRef.current.click()}
                        className="text-primary font-medium cursor-pointer"
                      >
                        Browse
                      </span>
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, form, name)}
                />
              </div>
            ) : (
              <div className="w-[300px]">
                <div className="flex items-center justify-between rounded p-1">
                  <p className="text-fs_12 truncate">{fileData.name}</p>
                  <button
                    type="button"
                    onClick={() => handleRemoveVideo(form, name)}
                    className="text-[9px] bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                  >
                    Remove
                  </button>
                </div>
                <video
                  src={fileData.url}
                  controls
                  className="w-full rounded-lg mb-2"
                />
              </div>
            )}

            {error && (
              <span className="text-red-500 text-sm">
                <ErrorMessage name={name} />
              </span>
            )}
          </div>
        );
      }}
    </Field>
  );
};
